import React from "react";
import { Link, useParams } from "react-router-dom";
import { PageShell } from "@/components/layout/PageShell";
import { Seo } from "@/components/seo/Seo";

interface ContributorPublic {
  slug: string;
  name: string;
  bio: string;
  bioSnippet: string;
  avatarUrl?: string;
  didOrPersonRef?: string;
}

interface PostListItem {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  publishedAt: string | null;
  category?: string;
}

interface ContribResponse {
  contributor: ContributorPublic;
  posts: PostListItem[];
}

function formatDate(iso: string | null): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

export function VoicesByAuthor() {
  const { authorSlug } = useParams<{ authorSlug: string }>();
  const [state, setState] = React.useState<
    | { status: "loading" }
    | { status: "found"; data: ContribResponse }
    | { status: "not_found" }
    | { status: "error"; message: string }
  >({ status: "loading" });

  React.useEffect(() => {
    if (!authorSlug) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(
          `/api/voices/contributors/${encodeURIComponent(authorSlug)}`
        );
        if (res.status === 404) {
          if (!cancelled) setState({ status: "not_found" });
          return;
        }
        if (!res.ok) throw new Error(`status ${res.status}`);
        const data = (await res.json()) as ContribResponse;
        if (!cancelled) setState({ status: "found", data });
      } catch (err) {
        if (!cancelled) setState({ status: "error", message: String(err) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authorSlug]);

  if (state.status === "loading") {
    return (
      <PageShell title="Loading…" status="loading">
        <Seo title="Loading…" description="" path={`/voices/by/${authorSlug ?? ""}`} noindex />
        <p className="font-mono text-xs text-ink-3 uppercase tracking-widest">
          Loading…
        </p>
      </PageShell>
    );
  }

  if (state.status === "not_found") {
    return (
      <PageShell title="Not found" status="404">
        <Seo
          title="Contributor not found"
          description=""
          path={`/voices/by/${authorSlug ?? ""}`}
          noindex
        />
        <p className="font-serif text-lg text-ink-2 leading-relaxed">
          No contributor found at that path.{" "}
          <Link to="/voices/contributors" className="text-claude underline">
            See all contributors →
          </Link>
        </p>
      </PageShell>
    );
  }

  if (state.status === "error") {
    return (
      <PageShell title="Error" status="error">
        <Seo
          title="Error"
          description=""
          path={`/voices/by/${authorSlug ?? ""}`}
          noindex
        />
        <div className="border border-red-500/40 bg-red-500/5 px-4 py-3 font-mono text-xs text-red-500">
          Could not load contributor: {state.message}
        </div>
      </PageShell>
    );
  }

  const { contributor, posts } = state.data;

  return (
    <>
      <Seo
        title={`${contributor.name} — Voices — The .person Foundation`}
        description={contributor.bioSnippet || `Posts by ${contributor.name}.`}
        path={`/voices/by/${contributor.slug}`}
      />
      <PageShell title={contributor.name} subtitle={contributor.bioSnippet} status="open">
        <div className="mt-8">
          {contributor.bio && (
            <div className="max-w-2xl mb-12">
              <p className="font-serif text-base text-ink-2 leading-relaxed whitespace-pre-line">
                {contributor.bio}
              </p>
              {contributor.didOrPersonRef && (
                <p className="mt-4 font-mono text-xs text-ink-3">
                  <Link
                    to={contributor.didOrPersonRef}
                    className="underline hover:text-claude"
                  >
                    .person profile →
                  </Link>
                </p>
              )}
            </div>
          )}

          <p className="font-mono text-[10px] text-ink-3 uppercase tracking-widest mb-4">
            Posts
          </p>

          {posts.length === 0 ? (
            <p className="font-serif text-base text-ink-2 leading-relaxed">
              No published posts yet.
            </p>
          ) : (
            <ul className="divide-y divide-hairline border-t border-b border-hairline">
              {posts.map((post) => (
                <li key={post.id} className="py-6">
                  <article>
                    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-2">
                      <p className="font-mono text-[10px] text-ink-3 uppercase tracking-widest">
                        {formatDate(post.publishedAt)}
                      </p>
                      {post.category && (
                        <p className="font-mono text-[10px] text-ink-3 uppercase tracking-widest">
                          {post.category}
                        </p>
                      )}
                    </div>
                    <h2 className="font-serif text-xl text-ink mb-2">
                      <Link
                        to={`/voices/${post.slug}`}
                        className="hover:text-claude transition-colors"
                      >
                        {post.title}
                      </Link>
                    </h2>
                    <p className="font-serif text-base text-ink-2 leading-relaxed">
                      {post.excerpt}
                    </p>
                  </article>
                </li>
              ))}
            </ul>
          )}

          <p className="mt-12 font-mono text-[10px] text-ink-3 uppercase tracking-widest">
            <Link to="/voices/contributors" className="underline hover:text-ink mr-6">
              ← all contributors
            </Link>
            <Link to="/voices" className="underline hover:text-ink">
              all voices
            </Link>
          </p>
        </div>
      </PageShell>
    </>
  );
}
